import React from 'react';
import FONTS from '../../data/fonts.json';

const FONT_BASE_PATH = '/img/fontgen';

function charToGlyph(char, filenamePattern, folder) {
    if (char === ' ') return { src: `${FONT_BASE_PATH}/${folder}/+.gif`, draw: true };
    const lower = char.toLowerCase();
    if (lower >= 'a' && lower <= 'z') {
        const filename = filenamePattern === 'letter1' ? `${lower}1.gif` : `${lower}.gif`;
        return { src: `${FONT_BASE_PATH}/${folder}/${filename}`, draw: true };
    }
    return null;
}

function loadImage(src) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
    });
}

async function generateFontImage(text, folder, filenamePattern, offset = 0) {
    const glyphs = text.split('').map((char) => charToGlyph(char, filenamePattern, folder));
    const loadedImgs = await Promise.all(
        glyphs.map((g) => (g?.src ? loadImage(g.src) : Promise.resolve(null)))
    );
    if (!loadedImgs.some(Boolean)) return null;

    let totalWidth = 0;
    let currentX = 0;
    loadedImgs.forEach((img, i) => {
        if (img) {
            totalWidth = Math.max(totalWidth, currentX + img.naturalWidth);
            if (i < loadedImgs.length - 1) {
                currentX += img.naturalWidth + offset;
            }
        }
    });

    const maxHeight = loadedImgs.reduce((max, img) => Math.max(max, img ? img.naturalHeight : 0), 0);
    if (totalWidth === 0 || maxHeight === 0) return null;

    const canvas = document.createElement('canvas');
    canvas.width = totalWidth;
    canvas.height = maxHeight;
    const ctx = canvas.getContext('2d');

    let x = 0;
    for (let i = 0; i < loadedImgs.length; i++) {
        const img = loadedImgs[i];
        const g = glyphs[i];
        if (img && g) {
            if (g.draw) ctx.drawImage(img, x, maxHeight - img.naturalHeight);
            x += img.naturalWidth + offset;
        }
    }
    return canvas.toDataURL('image/png');
}

// ── FancyFontBox ──────────────────────────────────────────────────────────────

const FancyFontBox = ({ isEditing, isOwnProfile, customisation, setCustomisationValue }) => {
    const [dataURL, setDataURL]         = React.useState(null);
    const [isGenerating, setIsGenerating] = React.useState(false);

    const text        = customisation?.fancyFontText ?? 'Welcome to my Profile!';
    const fontFolder  = customisation?.fancyFontType  ?? 'Habbowood';
    const isPixelated = customisation?.fancyFontPixelated ?? false;
    const isFitToBox  = customisation?.fancyFontFit ?? true;

    const fontConfig = FONTS.find(f => f.folder === fontFolder) || FONTS[0];

    React.useEffect(() => {
        let isMounted = true;
        if (text.length < 5) {
            setDataURL(null);
            return;
        }
        const generate = async () => {
            if (isMounted) setIsGenerating(true);
            const url = await generateFontImage(
                text,
                fontConfig.folder,
                fontConfig.filenamePattern,
                fontConfig.offset || 0
            );
            if (isMounted) {
                setDataURL(url);
                setIsGenerating(false);
            }
        };
        const timeoutId = setTimeout(generate, 300);
        return () => { isMounted = false; clearTimeout(timeoutId); };
    }, [text, fontConfig.folder, fontConfig.filenamePattern]);

    const stopProps = (e) => e.stopPropagation();

    return (
        <div className="w-full flex flex-col bg-offwhite border border-black/10">
            {isEditing && isOwnProfile && (
                <div className="flex flex-row items-center gap-1.5 px-2 py-2 border-b border-black/20 bg-black/5">
                    {/* Text Input */}
                    <input
                        type="text"
                        value={text}
                        onChange={(e) => setCustomisationValue('fancyFontText', e.target.value)}
                        placeholder="Text..."
                        maxLength={40}
                        onPointerDown={stopProps}
                        onMouseDown={stopProps}
                        className="flex-1 min-w-0 px-2 py-1 text-[11px] border border-black/30 bg-offwhite text-coal rounded focus:outline-none"
                    />

                    {/* Font Dropdown */}
                    <select
                        value={fontFolder}
                        onChange={(e) => setCustomisationValue('fancyFontType', e.target.value)}
                        onPointerDown={stopProps}
                        onMouseDown={stopProps}
                        className="w-24 px-1 py-1 text-[11px] border border-black/30 bg-offwhite text-coal rounded focus:outline-none"
                    >
                        {FONTS.map(f => (
                            <option key={f.folder} value={f.folder}>{f.name}</option>
                        ))}
                    </select>

                    {/* Pixelated Toggle */}
                    <button
                        onClick={() => setCustomisationValue('fancyFontPixelated', !isPixelated)}
                        onMouseDown={stopProps}
                        title="Toggle Pixelated Rendering"
                        className={`px-2 py-1 text-[9px] uppercase font-bold border border-black/30 rounded whitespace-nowrap transition-colors ${
                            isPixelated ? 'bg-gold text-white border-gold' : 'bg-white text-coal/50'
                        }`}
                    >
                        Pixelated
                    </button>

                    {/* Fit Toggle */}
                    <button
                        onClick={() => setCustomisationValue('fancyFontFit', !isFitToBox)}
                        onMouseDown={stopProps}
                        title="Toggle Fit to Box / True Size"
                        className={`px-2 py-1 text-[9px] uppercase font-bold border border-black/30 rounded whitespace-nowrap transition-colors ${
                            isFitToBox ? 'bg-gold text-white border-gold' : 'bg-white text-coal/50'
                        }`}
                    >
                        {isFitToBox ? 'Fit to Box' : '100%'}
                    </button>
                </div>
            )}

            <div className="p-3 w-full flex items-center justify-center min-h-[70px] overflow-hidden">
                {isGenerating && !dataURL ? (
                    <span className="text-sm text-coal/70 animate-pulse">Generating...</span>
                ) : dataURL ? (
                    <img
                        src={dataURL}
                        alt="Fancy Font Preview"
                        draggable={false}
                        className={`object-contain ${isFitToBox ? 'w-full h-auto' : 'max-w-full h-auto'}`}
                        style={{
                            imageRendering: isPixelated ? 'pixelated' : 'auto'
                        }}
                    />
                ) : (
                    <span className="text-sm text-coal/70 italic">Enter at least 5 characters</span>
                )}
            </div>
        </div>
    );
};

export default FancyFontBox;